import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import { Application } from "@core/Application";
import { HollowedButton, NormalButton } from "@shared/designSystem/Button";
import ClickableWrapper from "@shared/designSystem/ClickableWrapper";
import { HorizontalContainer, VerticalContainer } from "@shared/designSystem/Containers";
import { RadioButton } from "@shared/designSystem/RadioButton";
import { Text } from "@shared/designSystem/Texts";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { NovedadesExportService } from "@services/NovedadesExportService";
import { useMemo } from "react";
import { useConfiguracionNovedadesHorasExtraViewModel } from "./useConfiguracionNovedadesHorasExtraViewModel";

interface ConfiguracionNovedadesHorasExtraProps {
    orgID: string;
}

const modeDescription = (mode: string): string => {
    switch (mode) {
        case "ALL_EXTRA_AS_REGULAR":
            return "Todas las horas trabajadas se informan como horas regulares.".localized();
        case "SHIFT_EXTRA_AS_50":
            return "Los minutos extra del turno se informan como horas extra al 50%.".localized();
        case "SATURDAY_CUTOFF":
            return "Las horas extra de sábados se dividen por horario de corte; domingos, feriados y días no laborales van al 100%.".localized();
        default:
            return mode;
    }
};

const scopeText = (scope: string): string => {
    return scope === "ORGANIZATION" ? "Organización".localized() : "";
};

const ConfiguracionNovedadesHorasExtra: React.FC<ConfiguracionNovedadesHorasExtraProps> = ({ orgID }) => {
    const service = useMemo(() => {
        return new NovedadesExportService(Application.shared().coreDependencies.httpClient);
    }, []);

    const viewModel = useConfiguracionNovedadesHorasExtraViewModel({
        orgID,
        service,
    });

    const handleSave = async () => {
        const didSave = await viewModel.save();
        if (didSave) {
            ToastManager.shared().showSuccessMessage("Configuración guardada.".localized());
        } else {
            ToastManager.shared().showErrorMessage("No se pudo guardar la configuración.".localized());
        }
    };

    const selectProfile = (profileID: string) => {
        if (viewModel.saving) {
            return;
        }
        viewModel.setSelectedProfileID(profileID);
    };

    if (viewModel.loading) {
        return (
            <VerticalContainer height="100%" alignment="center" justifyContent="center">
                <QontyLoading text={"Cargando configuración...".localized()} withQonty={true}/>
            </VerticalContainer>
        );
    }

    if (viewModel.error && viewModel.profiles.length === 0) {
        return (
            <VerticalContainer height="100%" alignment="center" justifyContent="center">
                <QontyEmptyState text={viewModel.error.localized()} darkMode={false} />
            </VerticalContainer>
        );
    }

    return (
        <VerticalContainer
            width="100%"
            gap={designSystemStyles.gapLg}
            alignment="stretch"
        >
            <VerticalContainer gap={designSystemStyles.gapSm} margin="0">
                <Text text={"**Cálculo de horas extra**".localized()} markdown={true} />
                <Text
                    text={"Seleccioná cómo se calculan las horas extra 50% y 100% al crear una exportación de novedades.".localized()}
                    color={designSystemStyles.colorText}
                />
            </VerticalContainer>

            <VerticalContainer gap={designSystemStyles.gapMd} margin="0">
                {viewModel.profiles.map((profile) => {
                    const selected = profile.profileID === viewModel.selectedProfileID;
                    return (
                        <ClickableWrapper
                            key={profile.profileID}
                            onClick={() => selectProfile(profile.profileID)}
                            disabled={viewModel.saving}
                            style={{
                                width: "100%",
                                borderRadius: designSystemStyles.radiusItem,
                            }}
                        >
                            <HorizontalContainer
                                alignment="flex-start"
                                gap={designSystemStyles.gapMd}
                                padding={designSystemStyles.paddingMd}
                                borderRadius={designSystemStyles.radiusItem}
                                backgroundColor={selected ? designSystemStyles.colorBackgroundGray2 : designSystemStyles.colorBackgroundWhite}
                            >
                                <VerticalContainer width="24px" padding="4px 0 0 0" margin="0">
                                    <RadioButton
                                        selected={selected}
                                        disabled={viewModel.saving}
                                        onClick={() => selectProfile(profile.profileID)}
                                        tabIndex={-1}
                                    />
                                </VerticalContainer>
                                <VerticalContainer gap={designSystemStyles.gapXxxSm} margin="0">
                                    <HorizontalContainer gap={designSystemStyles.gapSm} margin="0" width="auto">
                                        <Text text={`**${(profile.label ?? '').trim()}**`} markdown={true} />
                                        <Text text={scopeText(profile.scope)} color={designSystemStyles.colorText} />
                                    </HorizontalContainer>
                                    <Text text={profile.description || modeDescription(profile.mode)} color={designSystemStyles.colorText} />
                                    {profile.saturdayCutoffTime && (
                                        <Text
                                            text={`${"Hora de corte".localized()}: ${profile.saturdayCutoffTime}`}
                                            color={designSystemStyles.colorText}
                                        />
                                    )}
                                </VerticalContainer>
                            </HorizontalContainer>
                        </ClickableWrapper>
                    );
                })}
            </VerticalContainer>

            {viewModel.error && (
                <Text text={viewModel.error.localized()} color={designSystemStyles.colorError} />
            )}

            <HorizontalContainer justifyContent="flex-end" gap={designSystemStyles.gapMd}>
                <HollowedButton
                    text={"Cancelar".localized()}
                    onClick={viewModel.cancel}
                    disabled={!viewModel.hasChanges || viewModel.saving}
                    width="160px"
                />
                <NormalButton
                    text={viewModel.saving ? "Guardando...".localized() : "Guardar".localized()}
                    onClick={handleSave}
                    disabled={!viewModel.hasChanges || viewModel.saving}
                    width="160px"
                />
            </HorizontalContainer>
        </VerticalContainer>
    );
};

export default ConfiguracionNovedadesHorasExtra;
